import React, { createRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Nullable } from '../Shared/Types';
import styles from './ItemsLineCutter.module.scss';

const rebuildRefs = (
    itemCount: number,
    refs: React.RefObject<HTMLSpanElement>[]
): React.RefObject<HTMLSpanElement>[] => {
    return Array(itemCount)
        .fill(undefined)
        .map((_, i) => refs[i] || createRef());
};

/**
 * @description A wrapping component that cuts off items on the x-axis if they extend out of the bounds of the containing component.
 * Inspired (Copied and altered) from ItemsLineWrapper in the main client.
 * Note: this component is only suitable for 1 row.
 */
export function ItemsLineCutter<T>(props: {
    items: T[];
    join?: React.ReactNode;
    formatOverflowCount(overflowCount: number): React.ReactElement | null;
    renderItem(item: T): React.ReactNode;
}): Nullable<React.ReactElement> {
    const [visibleItems, setVisibleItems] = useState(props.items.length);
    const [itemRefs, setItemRefs] = useState<React.RefObject<HTMLSpanElement>[]>(rebuildRefs(props.items.length, []));
    const containerRef = useRef<HTMLDivElement>(null);
    const overflowRef = useRef<HTMLSpanElement>(null);
    const itemsLengthForResize = useRef<number>(props.items.length);

    useEffect(() => {
        setItemRefs(rebuildRefs(props.items.length, itemRefs));
        itemsLengthForResize.current = props.items.length;
    }, [props.items.length]);

    useEffect(() => {
        setVisibleItems(props.items.length);
    }, [props.items]);

    useEffect(() => {
        function resetVisibleItems() {
            setVisibleItems(itemsLengthForResize.current + 1);
        }

        window.addEventListener('resize', resetVisibleItems);

        return () => {
            window.removeEventListener('resize', resetVisibleItems);
        };
    }, []);

    const calculateVisible = (): void => {
        if (containerRef.current === null) return;

        const containerMaxRight = containerRef.current.getBoundingClientRect().right;

        let visibleCount = itemRefs.reduce<number>((count, itemRef) => {
            if (itemRef.current === null) return count;
            const itemMaxRight = itemRef.current.getBoundingClientRect().right;
            if (itemMaxRight > containerMaxRight) return count;
            return count + 1;
        }, 0);

        // overflow text needs to be handled separately because we handle ref being null differently to other items
        if (overflowRef.current !== null && overflowRef.current.getBoundingClientRect().right > containerMaxRight) {
            visibleCount = visibleCount - 1;
        }

        // only update downwards to prevent visual ping-pong
        if (visibleCount >= visibleItems) {
            return;
        }

        setVisibleItems(visibleCount);
    };

    useLayoutEffect(() => {
        calculateVisible();
    }, [visibleItems, containerRef.current, itemRefs, overflowRef.current]);

    if (props.items.length <= 0) return null;

    const hiddenItems = props.items.length - visibleItems;

    return (
        <div className={styles.container} ref={containerRef}>
            {props.items.slice(0, visibleItems).map((e, i, array) => (
                <React.Fragment key={i}>
                    <span key={i} ref={itemRefs[i]}>
                        {props.renderItem(e)}
                    </span>
                    {(i < array.length && props.join) || null}
                </React.Fragment>
            ))}
            {hiddenItems > 0 && (
                <span ref={overflowRef} className={styles.overflowCount}>
                    {props.formatOverflowCount(hiddenItems)}
                </span>
            )}
        </div>
    );
}
